import React from 'react';
import { SelectButton } from '../buttons/SelectButton/SelectButton';

import './selection-group.css';

/**
 * A reusable selection group component that manages exclusive selection
 * @param {Object} props - Component props
 * @param {string} props.title - Title of the selection group
 * @param {string} props.description - Description text under the title
 * @param {string[]} props.options - List of options to select from
 * @param {string[]} props.selectedOptions - Currently selected options
 * @param {function} props.onSelect - Function to handle option selection
 * @param {function} [props.filterOptions] - Optional function to filter options
 * @param {string} [props.className] - Optional CSS class name
 * @param {number} [props.rowSize] - If set, splits buttons into rows of this size; rows after the first are indented
 * @param {boolean} [props.isOptional] - If true, renders a styled "(Optional)" tag next to the title
 */

export const SelectionGroup = ({
  title,
  description = "",
  options,
  selectedOptions,
  onSelect,
  filterOptions,
  className,
  rowSize,
  isOptional = false,
}) => {
  const filteredOptions = filterOptions
    ? options.filter(filterOptions)
    : options;

  const renderButtons = (opts) =>
    opts.map((option) => (
      <SelectButton
        key={option}
        selected={selectedOptions.includes(option)}
        onClick={() => onSelect(option)}
      >
        {option}
      </SelectButton>
    ));

  const renderButtonRows = () => {
    if (!rowSize || filteredOptions.length <= rowSize) {
      return (
        <div className={`select-button-group ${className || ''}`}>
          {renderButtons(filteredOptions)}
        </div>
      );
    }

    const rows = [];
    for (let i = 0; i < filteredOptions.length; i += rowSize) {
      rows.push(filteredOptions.slice(i, i + rowSize));
    }

    return (
      <div className="select-button-rows">
        {rows.map((row, idx) => (
          <div
            key={idx}
            className={`select-button-group ${className || ''} ${idx > 0 ? 'select-button-row-indented' : ''}`}
          >
            {renderButtons(row)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="section-container">
      {title && (
        <div className="section-text-container">
          <h2 className="section-title">
            {title}
            {isOptional && <span className="section-title-optional"> Optional</span>}
          </h2>
          {description && <p className="section-body-text">{description}</p>}
        </div>
      )}
      {renderButtonRows()}
    </div>
  );
};

/**
 * Hook to manage selection state for a group of options
 * @param {function} [initialSelector] - Optional function to set initial state
 * @returns {[string[], function]} - Array of selected options and selection function
 */
export const useSelectionGroup = (initialSelector) => {
  const [selectedOptions, setSelectedOptions] = React.useState(
    initialSelector ? initialSelector() : []
  );

  const handleSelection = (option) => {
    setSelectedOptions(prevSelected => {
      // If option is already selected, remove it
      if (prevSelected.includes(option)) {
        return prevSelected.filter(item => item !== option);
      }
      // If option is not selected, add it (replacing any previous selection)
      return [option];
    });
  };

  return [selectedOptions, handleSelection, setSelectedOptions];
};